// pages/Dashboard/Invoices.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaEye,
  FaEdit,
  FaArchive,
  FaUndo,
  FaReceipt,
  FaFileInvoiceDollar,FaFileExcel, FaCalendarAlt 
} from 'react-icons/fa';
import '../Css/Invoices.css';

import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl'; 

 

const STATUS_STYLES = {
  paid: { label: 'Paid', cls: 'status-paid' },
  partial: { label: 'Partial', cls: 'status-partial' },
  due: { label: 'Due', cls: 'status-due' },
};

const FILTERS = [
  { key: '', label: 'All Status' },
  { key: 'paid', label: 'Paid' },
  { key: 'partial', label: 'Partial' },
  { key: 'due', label: 'Due' },
];

function Invoices() {
  const navigate = useNavigate();
  const toast = useToast();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);


  const [confirmAction, setConfirmAction] = useState(null); 

const [fromDate, setFromDate] = useState('');
const [toDate, setToDate] = useState('');
const [exporting, setExporting] = useState(false);



  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });


 

// Update fetchInvoices to include date filters
const fetchInvoices = useCallback(async () => {
  setLoading(true);
  try {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    params.append('archived', showArchived ? 'true' : 'false');

    const res = await fetch(`${baseurl}/invoices?${params.toString()}`, {
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch invoices');

    setInvoices(data.data.invoices);
  } catch (err) {
    toast(err.message || 'Failed to fetch invoices', 'error');
  } finally {
    setLoading(false);
  }
}, [search, statusFilter, fromDate, toDate, showArchived]);

// Add export handler
const handleExportExcel = async () => {
  setExporting(true);
  try {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    params.append('archived', showArchived ? 'true' : 'false');

    const res = await fetch(`${baseurl}/invoices/export?${params.toString()}`, {
      headers: authHeaders(),
    });

    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.message || 'Failed to export invoices');
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast('Invoices exported successfully', 'success');
  } catch (err) {
    toast(err.message || 'Failed to export invoices', 'error');
  } finally {
    setExporting(false);
  }
};

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleView = (uuid) => {
  navigate(`/dashboard/invoice/${uuid}`);
};

const requestEdit = (inv) => {
  setConfirmAction({ type: 'edit', uuid: inv.uuid, invoiceNumber: inv.invoice_number });
};

const requestToggleArchive = (inv) => {
  setConfirmAction({
    type: inv.is_archived ? 'restore' : 'archive',
    uuid: inv.uuid,
    invoiceNumber: inv.invoice_number,
  });
};

const closeConfirm = () => setConfirmAction(null);

const confirmProceed = async () => {
  if (!confirmAction) return;

  if (confirmAction.type === 'edit') {
    navigate(`/dashboard/billing/${confirmAction.uuid}?action=edit`);
    closeConfirm();
    return;
  }

  // archive or restore
  try {
    const res = await fetch(`${baseurl}/invoices/${confirmAction.uuid}/archive`, {
      method: 'PATCH',
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update invoice');

    toast(data.message || 'Invoice updated', 'success');
    await fetchInvoices();
  } catch (err) {
    toast(err.message || 'Failed to update invoice', 'error');
  } finally {
    closeConfirm();
  }
};

  return (
    <div className="invoices-page">
      <div className="invoices-header">
        <div>
          <h2>Invoices</h2>
          <p>View, edit, and manage all client invoices</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/dashboard/billing')}>
          <FaFileInvoiceDollar /> New Invoice
        </button>
      </div>

 
<div className="invoices-toolbar">
  <div className="input-group search-input">
    <FaSearch className="input-icon" />
    <input
      type="text"
      placeholder="Search by client name or invoice number"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />
  </div>

  <div className="date-filter-group">
    <FaCalendarAlt className="input-icon" />
    <input
      type="date"
      value={fromDate}
      onChange={(e) => setFromDate(e.target.value)}
      className="date-filter-input"
    />
    <span className="date-filter-sep">to</span>
    <input
      type="date"
      value={toDate}
      onChange={(e) => setToDate(e.target.value)}
      className="date-filter-input"
    />
  </div>

  <div className="filter-chips">
    {FILTERS.map((f) => (
      <button
        key={f.key}
        className={`filter-chip ${statusFilter === f.key ? 'active' : ''}`}
        onClick={() => setStatusFilter(f.key)}
      >
        {f.label}
      </button>
    ))}
  </div>

  <button
    className={`archive-toggle-btn ${showArchived ? 'active' : ''}`}
    onClick={() => setShowArchived((prev) => !prev)}
  >
    <FaArchive /> {showArchived ? 'Showing Archived' : 'Show Archived'}
  </button>

  <button className="export-excel-btn" onClick={handleExportExcel} disabled={exporting}>
    <FaFileExcel /> {exporting ? 'Exporting...' : 'Export Excel'}
  </button>
</div>

      <div className="invoices-table-wrapper">
        <table className="invoices-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Client</th>
              <th>Items</th>
              <th>Grand Total</th>
              <th>Status</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="7" className="invoices-empty-row">Loading invoices...</td>
              </tr>
            )}

            {!loading &&
              invoices.map((inv) => (
                <tr key={inv.uuid} className="invoice-row">
                  <td>
                    <div className="invoice-number-cell">
                      <FaReceipt className="invoice-icon" />
                      <strong>{inv.invoice_number}</strong>
                    </div>
                  </td>
                  <td>
                    <div>
                      <strong>{inv.client_name}</strong>
                      {inv.client_phone && <span className="table-sub">{inv.client_phone}</span>}
                    </div>
                  </td>
                  <td>{inv.item_count} item{inv.item_count !== 1 ? 's' : ''}</td>
                  <td className="table-total">₹{Number(inv.grand_total).toLocaleString()}</td>
                  <td>
                    <span className={`status-pill ${STATUS_STYLES[inv.status]?.cls}`}>
                      {STATUS_STYLES[inv.status]?.label}
                    </span>
                  </td>
                  <td>{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td>
                     
<div className="invoice-actions">
  <button className="icon-btn" title="View" onClick={() => handleView(inv.uuid)}>
    <FaEye />
  </button>
  {!inv.is_archived && (
    <button className="icon-btn" title="Edit" onClick={() => requestEdit(inv)}>
      <FaEdit />
    </button>
  )}
  <button
    className={`icon-btn ${inv.is_archived ? 'restore-btn' : 'archive-btn'}`}
    title={inv.is_archived ? 'Restore' : 'Archive'}
    onClick={() => requestToggleArchive(inv)}
  >
    {inv.is_archived ? <FaUndo /> : <FaArchive />}
  </button>
</div>
                  </td>
                </tr>
              ))}

            {!loading && invoices.length === 0 && (
              <tr>
                <td colSpan="7" className="invoices-empty-row">
                  No invoices found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>



    
{confirmAction && (
  <div className="modal-overlay" onClick={closeConfirm}>
    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
      <div className="confirm-modal-icon">
        {confirmAction.type === 'edit' && <FaEdit />}
        {confirmAction.type === 'archive' && <FaArchive />}
        {confirmAction.type === 'restore' && <FaUndo />}
      </div>
      <h3>
        {confirmAction.type === 'edit' && 'Edit this invoice?'}
        {confirmAction.type === 'archive' && 'Archive this invoice?'}
        {confirmAction.type === 'restore' && 'Restore this invoice?'}
      </h3>
      <p>
        {confirmAction.type === 'edit' &&
          `You're about to edit invoice ${confirmAction.invoiceNumber}. Any changes will overwrite the existing invoice details.`}
        {confirmAction.type === 'archive' &&
          `Invoice ${confirmAction.invoiceNumber} will be moved to archived invoices and hidden from the active list.`}
        {confirmAction.type === 'restore' &&
          `Invoice ${confirmAction.invoiceNumber} will be restored back to the active invoices list.`}
      </p>
      <div className="confirm-modal-actions">
        <button className="btn-outline full-width" onClick={closeConfirm}>
          Cancel
        </button>
        <button
          className={`full-width ${confirmAction.type === 'archive' ? 'btn-danger' : 'btn-primary'}`}
          onClick={confirmProceed}
        >
          {confirmAction.type === 'edit' && 'Yes, Edit'}
          {confirmAction.type === 'archive' && 'Yes, Archive'}
          {confirmAction.type === 'restore' && 'Yes, Restore'}
        </button>
      </div>
    </div>
  </div>
)}



    </div>
  );
}

export default Invoices;